/**
 * Features Slider
 * @requires https://swiperjs.com/
 */

export default (() => {
  /* eslint-disable no-undef, no-unused-vars  */

  // Get labels
  const navEl = document.querySelector('#features-slider-navigation')
  let featureLabels = []
  const raw = navEl.getAttribute('data-labels')

  try {
    featureLabels = JSON.parse(raw.replace(/'/g, '"'))
  } catch (err) {
    featureLabels = raw.split(',').map((s) => s.trim())
  }

  // Create circular progress SVG for mobile
  const createMobileProgress = (index, activeIndex) => {
    const size = 22
    const strokeWidth = 1
    const radius = (size - strokeWidth) / 2
    const circum = 2 * Math.PI * radius
    const center = size / 2
    const isActive = activeIndex === index

    return `
      <div class="mobile-progress shrink-0 sm:hidden overflow-hidden" style="${isActive ? `width: ${size + 8}px` : 'width: 0'}">
        <svg class="mobile-progress-svg -rotate-90" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <circle 
            cx="${center}" 
            cy="${center}" 
            r="${radius}" 
            stroke="rgba(255,255,255,0.25)" 
            stroke-width="${strokeWidth}" 
            fill="rgba(255,255,255,0.12)"
          />
          <circle 
            class="mobile-progress-arc" 
            data-index="${index}"
            cx="${center}" 
            cy="${center}" 
            r="${radius}" 
            stroke="white" 
            stroke-width="${strokeWidth}" 
            stroke-linecap="round"
            fill="none" 
            stroke-dasharray="${circum}" 
            stroke-dashoffset="${circum}"
            style="transition: stroke-dashoffset 0.1s ease"
          />
        </svg>
      </div>
    `
  }

  const buildCircularPagination = (activeIndex) => {
    const size = 590
    const cx = size / 2
    const cy = size / 2
    const r = 293
    const circum = 2 * Math.PI * r

    const html = `
    <div class="flex flex-col items-start sm:items-center gap-4 sm:gap-0">
      <!-- Label 1 on both desktop and mobile -->
      <div class="group circle-label relative z-1 inline-flex sm:flex-col items-center sm:gap-4 font-heading text-xl/6 sm:text-2xl/7 md:text-[1.75rem]/9 text-white uppercase sm:text-center cursor-pointer sm:-mb-2" data-index="0">
        ${createMobileProgress(0, activeIndex)}
        <div class="transition-opacity duration-300 group-hover:opacity-100 ${activeIndex === 0 ? 'opacity-100' : 'opacity-60 sm:opacity-45'}">${featureLabels[0]}</div>
        <div class="shrink-0 size-3 border rounded-full transition-colors duration-250 backdrop-blur-lg hidden sm:block ${activeIndex === 0 ? 'bg-white border-white' : 'bg-white/20 border-white/50'}"></div>
      </div>
      
      <!-- Label 2 on mobile, positioned differently on desktop -->
      <div class="group circle-label relative z-1 inline-flex sm:hidden items-center font-heading text-xl/6 sm:text-2xl/7 md:text-[1.75rem]/9 text-white uppercase cursor-pointer" data-index="1">
        ${createMobileProgress(1, activeIndex)}
        <div class="transition-opacity duration-300 group-hover:opacity-100 ${activeIndex === 1 ? 'opacity-100' : 'opacity-60'}">${featureLabels[1]}</div>
      </div>
      
      <!-- Label 3 on mobile, positioned differently on desktop -->
      <div class="group circle-label relative z-1 inline-flex sm:hidden items-center font-heading text-xl/6 sm:text-2xl/7 md:text-[1.75rem]/9 text-white uppercase cursor-pointer" data-index="2">
        ${createMobileProgress(2, activeIndex)}
        <div class="transition-opacity duration-300 group-hover:opacity-100 ${activeIndex === 2 ? 'opacity-100' : 'opacity-60'}">${featureLabels[2]}</div>
      </div>
      
      <!-- Label 4 on mobile, positioned differently on desktop -->
      <div class="group circle-label relative z-1 inline-flex sm:hidden items-center font-heading text-xl/6 sm:text-2xl/7 md:text-[1.75rem]/9 text-white uppercase cursor-pointer" data-index="3">
        ${createMobileProgress(3, activeIndex)}
        <div class="transition-opacity duration-300 group-hover:opacity-100 ${activeIndex === 3 ? 'opacity-100' : 'opacity-60'}">${featureLabels[3]}</div>
      </div>
      
      <!-- Desktop navigation (labels) -->
      <div class="hidden sm:flex flex-col sm:flex-row w-full items-start sm:items-center gap-4 sm:gap-0">
        <div class="group circle-label relative z-1 inline-flex flex-1 items-center gap-4 font-heading text-xl/6 sm:text-2xl/7 md:text-[1.75rem]/9 text-white uppercase sm:text-right cursor-pointer sm:-mr-2" data-index="3">
          <div class="transition-opacity duration-300 group-hover:opacity-100 ${activeIndex === 3 ? 'opacity-100' : 'sm:opacity-45'}">${featureLabels[3]}</div>
          <div class="shrink-0 size-3 border rounded-full transition-colors duration-250 backdrop-blur-lg block ${activeIndex === 3 ? 'bg-white border-white' : 'bg-white/20 border-white/50'}"></div>
        </div>
        <svg class="w-full -rotate-90 block" viewBox="0 0 ${size} ${size}" style="max-width: ${size}px">
          <circle r="${r}" cx="${cx}" cy="${cy}" stroke="rgba(255,255,255,0.4)" stroke-width="1" fill="rgba(255,255,255,0.1)" />
          <circle id="progress-arc" r="${r}" cx="${cx}" cy="${cy}" stroke="white" stroke-width="3" stroke-linecap="round" fill="none" stroke-dasharray="${circum}" stroke-dashoffset="${circum}" />
        </svg>
        <div class="group circle-label relative z-1 inline-flex flex-1 items-center gap-4 font-heading text-xl/6 sm:text-2xl/7 md:text-[1.75rem]/9 text-white uppercase cursor-pointer sm:-ml-2" data-index="1">
          <div class="shrink-0 size-3 border rounded-full transition-colors duration-250 backdrop-blur-lg block ${activeIndex === 1 ? 'bg-white border-white' : 'bg-white/20 border-white/50'}"></div>
          <div class="transition-opacity duration-300 group-hover:opacity-100 ${activeIndex === 1 ? 'opacity-100' : 'sm:opacity-45'}">${featureLabels[1]}</div>
        </div>
      </div>
      <div class="group circle-label relative z-1 hidden sm:inline-flex sm:flex-col items-center gap-4 font-heading text-xl/6 sm:text-2xl/7 md:text-[1.75rem]/9 text-white uppercase sm:text-center cursor-pointer sm:-mt-2" data-index="2">
        <div class="shrink-0 size-3 border rounded-full transition-colors duration-250 backdrop-blur-lg block ${activeIndex === 2 ? 'bg-white border-white' : 'bg-white/20 border-white/50'}"></div>
        <div class="transition-opacity duration-300 group-hover:opacity-100 ${activeIndex === 2 ? 'opacity-100' : 'sm:opacity-45'}">${featureLabels[2]}</div>
      </div>
    </div>
  `

    return html
  }

  // Update progress arc based on slide index
  const updateProgressArc = (swiper) => {
    const arc = document.querySelector('#progress-arc')
    if (arc) {
      const r = arc.r.baseVal.value
      const circum = 2 * Math.PI * r
      const segment = circum / featureLabels.length

      const offset = circum - segment * swiper.realIndex
      arc.style.strokeDashoffset = offset
    }

    // Reset all mobile progress arcs
    const mobileArcs = document.querySelectorAll('.mobile-progress-arc')
    mobileArcs.forEach((arc) => {
      const radius = arc.r.baseVal.value
      const circum = 2 * Math.PI * radius
      const index = parseInt(arc.dataset.index, 10)

      // Reset to empty or full based on whether it's the active slide
      arc.style.strokeDashoffset = index === swiper.realIndex ? circum : circum
    })
  }

  // Animate arc during autoplay (desktop)
  const updateArcByTime = (swiper, timeLeft, percentage) => {
    const arc = document.querySelector('#progress-arc')
    if (arc) {
      const r = arc.r.baseVal.value
      const circum = 2 * Math.PI * r
      const segment = circum / featureLabels.length

      const startOffset = circum - segment * swiper.realIndex

      const current = startOffset - (1 - percentage) * segment
      arc.style.strokeDashoffset = current
    }

    // Animate mobile progress arc for active slide
    const mobileArcs = document.querySelectorAll('.mobile-progress-arc')
    mobileArcs.forEach((arc) => {
      const index = parseInt(arc.dataset.index, 10)
      if (index === swiper.realIndex) {
        const radius = arc.r.baseVal.value
        const circum = 2 * Math.PI * radius

        // Animate from full circle to empty
        const current = circum - (1 - percentage) * circum
        arc.style.strokeDashoffset = current
      }
    })
  }

  // Init
  const contentSlider = new Swiper('#features-slider-content', {
    effect: 'fade',
    fadeEffect: { crossFade: true },
    loop: true,
    allowTouchMove: false,
    autoplay: {
      delay: 10000,
      disableOnInteraction: false,
    },
    pagination: {
      el: '#features-slider-navigation',
      clickable: true,
      type: 'custom',
      renderCustom: (swiper, current) => {
        return buildCircularPagination(current - 1)
      },
    },
    on: {
      slideChange: updateProgressArc,
      autoplayTimeLeft: updateArcByTime,
    },
  })

  const imageSlider = new Swiper('#features-slider-images', {
    effect: 'fade',
    allowTouchMove: false,
    loop: true,
    speed: 500,
  })

  // Sync sliders
  contentSlider.controller.control = imageSlider

  // Click support for labels
  document.addEventListener('click', (e) => {
    const circleLabel = e.target.closest('.circle-label')
    if (!circleLabel) return
    const index = parseInt(circleLabel.dataset.index, 10)
    contentSlider.slideToLoop(index)
  })
  /* eslint-enable no-undef, no-unused-vars  */
})()
